import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import { PlayArrow, Stop, ZoomIn, ZoomOut, RestartAlt, GraphicEq } from '@mui/icons-material';
import { useColorMode } from '../contexts/ColorModeContext';
import { readThemeColor } from '../theme';

/**
 * VoicetrackSegueEditor
 *
 * A junction-relative segue editor. The timeline is measured in seconds with
 * t=0 placed at the END of the previous track (the natural junction point).
 *  - Negative time = the outro/tail of the previous track
 *  - Positive time = the next track playing
 *
 * The voicetrack starts on the previous track's outro marker (start next, or
 * fade out if start next is not set) — immediately where that track hands off.
 * "Sweep over intro" keeps the voice on the outro and slides the next track's
 * intro under the end of the voicetrack (the same timing the mixer uses), ducked
 * until the vocal. A music bed, when one is selected, is drawn and previewed
 * underneath the voice. A position saved by this editor is kept. Older saves,
 * which lined the voice up with the next intro, are moved onto the outro marker.
 *
 * There is no manual volume control. The surrounding tracks automatically dip
 * (duck) under the voicetrack for the length of the overlap, using the system
 * default duck level (`sweeper_duck_level_db`) and ramp (`crossfade_duration`),
 * exactly as the playout engine does. The ducking is drawn as an envelope and
 * applied in the live preview.
 */

const LANE_HEIGHT = 90;
const LANE_GAP = 6;
const RULER_HEIGHT = 22;

function readMarker(item, keys) {
  if (!item || typeof item !== 'object') return null;
  const bags = [item];
  if (item.markers && typeof item.markers === 'object') bags.push(item.markers);
  if (item.asset && typeof item.asset === 'object') {
    bags.push(item.asset);
    if (item.asset.markers && typeof item.asset.markers === 'object') bags.push(item.asset.markers);
  }
  for (const bag of bags) {
    for (const key of keys) {
      if (bag[key] == null || bag[key] === '') continue;
      const n = Number(bag[key]);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return null;
}

/**
 * Where the segue editor places a voicetrack, in seconds relative to the end
 * of the previous track (t = 0). Negative time sits on that track's outro.
 *
 * Returns the start time, whether a saved editor position was kept, the
 * previous track's visible length, and the outro marker (or null).
 */
export function resolveVoicetrackStart(previousItem, initialSegue = null) {
  const prevDur = Number(previousItem?.duration) || 0;
  const prevCueOut = readMarker(previousItem, ['cue_out', 'cueOut']) ?? (prevDur > 0 ? prevDur : 0);
  const prevSpan = prevCueOut || prevDur;
  const prevOutroAbs =
    readMarker(previousItem, ['start_next', 'startNext']) ??
    readMarker(previousItem, ['fade_out_start', 'fadeOutStart', 'extro']);

  let prevOutroRel = null;
  if (prevOutroAbs != null && prevSpan > 0) {
    const rel = prevOutroAbs - prevSpan;
    if (rel < -0.05) prevOutroRel = Math.round(rel * 10) / 10;
  }

  const savedRaw = initialSegue != null ? Number(initialSegue.vtStart) : NaN;
  const saved = Number.isFinite(savedRaw) ? Math.round(savedRaw * 10) / 10 : null;
  const respectSaved = saved != null && initialSegue?.anchor === 'outro';

  let vtStart = 0;
  if (respectSaved) vtStart = saved;
  else if (prevOutroRel != null) vtStart = prevOutroRel;
  else if (saved != null) vtStart = saved;

  return { vtStart, respectSaved, prevSpan, prevOutroRel };
}

/**
 * Where the next track starts, junction-relative, when the voicetrack sweeps
 * its intro. The mixer waits max(0, voice length − intro) after the voice
 * begins, so the intro sits under the end of the voicetrack. Returns null when
 * there is no intro to sweep.
 */
export function resolveSweepNextStart(vtStart, vtDur, nextIntro) {
  const start = Number(vtStart);
  const dur = Number(vtDur);
  const intro = Number(nextIntro);
  if (!Number.isFinite(start) || !(dur > 0) || !(intro > 0)) return null;
  const delay = Math.max(0, dur - intro);
  return Math.round((start + delay) * 10) / 10;
}

function drawWaveformSlice(ctx, buf, {
  timeToX,
  xToTime,
  pxPerSec,
  y0,
  height,
  startT,
  endT,
  originT = null,
  color,
  ampScale = 1,
  loop = false,
  envFn = null,
}) {
  if (!buf) return;
  const data = buf.getChannelData(0);
  const sr = buf.sampleRate;
  const centerY = y0 + height / 2;
  const xStart = Math.max(0, timeToX(startT));
  const xEnd = Math.min(ctx.canvas.width, timeToX(endT));
  const origin = originT == null ? startT : originT;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = Math.floor(xStart); x <= xEnd; x++) {
    const t = xToTime(x);
    let localT = t - origin;
    if (loop && buf.duration > 0) {
      localT = ((localT % buf.duration) + buf.duration) % buf.duration;
    } else if (localT < 0 || localT > buf.duration) {
      continue;
    }
    const sampleIdx = Math.floor(localT * sr);
    const blk = Math.max(1, Math.floor(sr / pxPerSec));
    let peak = 0;
    for (let j = 0; j < blk; j += Math.max(1, Math.floor(blk / 32))) {
      const v = Math.abs(data[sampleIdx + j] || 0);
      if (v > peak) peak = v;
    }
    const env = envFn ? envFn(t) : 1;
    const amp = peak * (height / 2) * 0.9 * ampScale * env;
    ctx.moveTo(x, centerY - amp);
    ctx.lineTo(x, centerY + amp);
  }
  ctx.stroke();
}

export default function VoicetrackSegueEditor({
  apiUrl = '',
  previousItem = null,
  nextItem = null,
  vtAudioUrl = null,
  vtDuration = 0,
  initialSegue = null,
  sweepOverIntro = false,
  musicBed = null,
  onChange = null,
}) {
  const placement = resolveVoicetrackStart(previousItem, initialSegue);
  const { mode: colorMode } = useColorMode();
  const [buffers, setBuffers] = useState({ previous: null, voicetrack: null, next: null });
  const [loading, setLoading] = useState({ previous: false, voicetrack: false, next: false });
  const [bedBuffer, setBedBuffer] = useState(null);
  const [bedLoading, setBedLoading] = useState(false);
  const [frozenView, setFrozenView] = useState(null);
  const [pxPerSec, setPxPerSec] = useState(24);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(placement.vtStart); // junction-relative seconds

  // System ducking defaults
  const [duckLevelDb, setDuckLevelDb] = useState(-6);
  const [fadeSec, setFadeSec] = useState(1);

  // Segue position (junction-relative seconds). Starts on the previous outro.
  const [vtStart, setVtStart] = useState(placement.vtStart);
  const [nextStart, setNextStart] = useState(
    placement.respectSaved ? (initialSegue?.nextStart ?? 0) : 0
  );

  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const audioCtxRef = useRef(null);
  const playSourcesRef = useRef([]);
  const rafRef = useRef(null);
  const playStartRef = useRef(0);
  const playFromRef = useRef(0);
  const dragRef = useRef(null);
  // A position this editor already saved is kept. Anything else snaps to the outro.
  const userMovedRef = useRef(placement.respectSaved);

  const prevSpan = placement.prevSpan;
  const prevOutroRel = placement.prevOutroRel;

  const nextDur = nextItem?.duration || 0;
  const nextIntro = readMarker(nextItem, ['intro', 'introLength', 'intro_length']) || 0;

  const vtDur = buffers.voicetrack?.duration || vtDuration || 0;
  const duckGain = Math.pow(10, duckLevelDb / 20);
  const parsedBedDb = musicBed?.volumeDb == null || musicBed.volumeDb === '' ? NaN : Number(musicBed.volumeDb);
  const bedVolumeDb = Number.isFinite(parsedBedDb) ? parsedBedDb : -12;
  const bedGain = Math.pow(10, bedVolumeDb / 20);
  const bedActive = !!musicBed?.enabled;
  // While sweeping, the next track follows the voice so its intro is covered.
  // Otherwise the next lane keeps whatever start the user (or a saved segue) set.
  const sweepNext = sweepOverIntro ? resolveSweepNextStart(vtStart, vtDur, nextIntro) : null;
  const displayedNextStart = sweepNext != null ? sweepNext : nextStart;

  const getCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  }, []);

  // Resolve an audio URL for a playlist item
  const resolveItemUrl = useCallback(
    (item) => {
      if (!item) return null;
      if ((item.type === 'voicetrack' || item.type === 'voice') && item.voicetrack_id) {
        return `${apiUrl}/api/voicetrack-enhanced/${item.voicetrack_id}/audio`;
      }
      if (item.asset_id) return `${apiUrl}/api/assets/${item.asset_id}/stream`;
      if (item.filepath) {
        let f = item.filepath;
        if (f.includes('/')) f = f.split('/').pop();
        if (f.startsWith('uploads/')) f = f.replace('uploads/', '');
        return `${apiUrl}/uploads/${f}`;
      }
      return null;
    },
    [apiUrl]
  );

  const loadGenRef = useRef({ previous: 0, voicetrack: 0, next: 0 });
  const loadBuffer = useCallback(
    async (url, lane) => {
      const gen = (loadGenRef.current[lane] || 0) + 1;
      loadGenRef.current[lane] = gen;
      const stillCurrent = () => loadGenRef.current[lane] === gen;
      if (!url) {
        setBuffers((b) => ({ ...b, [lane]: null }));
        setLoading((l) => ({ ...l, [lane]: false }));
        return;
      }
      setLoading((l) => ({ ...l, [lane]: true }));
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const arr = await res.arrayBuffer();
        const buf = await getCtx().decodeAudioData(arr.slice(0));
        if (stillCurrent()) setBuffers((b) => ({ ...b, [lane]: buf }));
      } catch (err) {
        console.warn(`[SegueEditor] Failed to load ${lane}:`, err.message);
        if (stillCurrent()) setBuffers((b) => ({ ...b, [lane]: null }));
      } finally {
        if (stillCurrent()) setLoading((l) => ({ ...l, [lane]: false }));
      }
    },
    [getCtx]
  );

  useEffect(() => {
    loadBuffer(resolveItemUrl(previousItem), 'previous');
  }, [previousItem, resolveItemUrl, loadBuffer]);
  useEffect(() => {
    loadBuffer(resolveItemUrl(nextItem), 'next');
  }, [nextItem, resolveItemUrl, loadBuffer]);
  useEffect(() => {
    loadBuffer(vtAudioUrl, 'voicetrack');
  }, [vtAudioUrl, loadBuffer]);

  // A specific music bed can be previewed under the dry voice. A random bed is
  // only chosen when the voicetrack is saved, so the lane shows a placeholder.
  useEffect(() => {
    const assetId = bedActive && musicBed?.mode === 'specific' ? musicBed.assetId : null;
    if (!assetId) {
      setBedBuffer(null);
      setBedLoading(false);
      return undefined;
    }
    let cancelled = false;
    setBedBuffer(null);
    setBedLoading(true);
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/assets/${assetId}/stream`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const arr = await res.arrayBuffer();
        const buf = await getCtx().decodeAudioData(arr.slice(0));
        if (!cancelled) setBedBuffer(buf);
      } catch (err) {
        console.warn('[SegueEditor] Failed to load music bed:', err.message);
        if (!cancelled) setBedBuffer(null);
      } finally {
        if (!cancelled) setBedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, bedActive, musicBed?.mode, musicBed?.assetId, getCtx]);

  // Fetch the system ducking defaults
  useEffect(() => {
    let cancelled = false;
    fetch(`${apiUrl}/api/system-settings`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : {}))
      .then((s) => {
        if (cancelled || !s) return;
        const duck = parseFloat(s.sweeper_duck_level_db);
        if (Number.isFinite(duck)) setDuckLevelDb(duck);
        const cf = parseFloat(s.crossfade_duration);
        if (Number.isFinite(cf)) setFadeSec(Math.max(0.2, Math.min(5, cf)));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const defaultVtStart = useCallback(
    () => resolveVoicetrackStart(previousItem, null).vtStart,
    [previousItem]
  );

  // Keep a fresh recording (and any older save) on the outro marker until the
  // user drags it. Sweep-over-intro only changes ducking, not this start.
  useEffect(() => {
    if (userMovedRef.current) return;
    setVtStart(defaultVtStart());
    setNextStart(0);
  }, [defaultVtStart]);

  // Emit changes to parent
  useEffect(() => {
    if (!onChange) return;
    onChange({
      vtStart,
      nextStart: displayedNextStart,
      sweep_over_intro: !!sweepOverIntro,
      next_intro: nextIntro,
      overlap_prev: vtStart < 0 ? -vtStart : 0,
      next_lead: vtStart + vtDur - displayedNextStart,
      vt_duration: vtDur,
      anchor: 'outro',
      duck: { level_db: duckLevelDb, fade_sec: fadeSec },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vtStart, displayedNextStart, vtDur, nextIntro, sweepOverIntro, duckLevelDb, fadeSec]);

  // ---- Timeline window ----
  const vtEnd = vtStart + vtDur;
  const windowLead = Math.min(
    prevSpan || 12,
    Math.max(12, vtStart < 0 ? -vtStart + 4 : 8, prevOutroRel != null ? -prevOutroRel + 2 : 0)
  );
  // Freeze the window for the duration of a drag. Including the live position
  // in tMin/tMax moves the time under the cursor on every move, so the clip
  // runs away from the pointer.
  const liveTMin = Math.min(vtStart, displayedNextStart, -windowLead, 0) - 1;
  const liveTMax = Math.max(vtEnd, displayedNextStart + Math.min(nextDur, 25), displayedNextStart + nextIntro + 2, 1) + 1;
  const tMin = frozenView ? frozenView.tMin : liveTMin;
  const tMax = frozenView ? frozenView.tMax : liveTMax;
  const timelineWidth = Math.max(640, (tMax - tMin) * pxPerSec);

  const timeToX = useCallback((t) => (t - tMin) * pxPerSec, [tMin, pxPerSec]);
  const xToTime = useCallback((x) => x / pxPerSec + tMin, [tMin, pxPerSec]);

  // Ducking envelope (returns linear gain 0..1) for a neighbour track.
  // The track ducks to `duckGain` between duckStart and duckEnd, ramping over fadeSec.
  const envAt = useCallback(
    (t, duckStart, duckEnd) => {
      if (duckEnd <= duckStart) return 1;
      const f = Math.max(0.05, fadeSec);
      if (t <= duckStart - f) return 1;
      if (t < duckStart) return 1 + (duckGain - 1) * ((t - (duckStart - f)) / f);
      if (t <= duckEnd) return duckGain;
      if (t < duckEnd + f) return duckGain + (1 - duckGain) * ((t - duckEnd) / f);
      return 1;
    },
    [duckGain, fadeSec]
  );

  // Duck windows
  const prevDuck = { start: Math.min(vtStart, 0), end: 0, active: vtStart < 0 };
  const nextDuckStart = Math.max(displayedNextStart, vtStart);
  const sweepOverlapEnd = sweepNext != null ? Math.min(vtEnd, displayedNextStart + nextIntro) : vtEnd;
  const nextDuck = {
    start: nextDuckStart,
    end: sweepOverlapEnd,
    active: sweepOverlapEnd > displayedNextStart + 0.05,
  };

  const drawBufferLane = useCallback(
    (ctx, buf, laneTopY, startT, endT, color, label, loadingLane, envFn, envWindow, underlay = null) => {
      const y0 = laneTopY;
      const h = LANE_HEIGHT;

      ctx.fillStyle = readThemeColor('--rs-bg', '#0d0d14');
      ctx.fillRect(0, y0, timelineWidth, h);
      ctx.strokeStyle = readThemeColor('--rs-border', '#23232e');
      ctx.strokeRect(0.5, y0 + 0.5, timelineWidth - 1, h - 1);

      ctx.fillStyle = readThemeColor('--rs-muted', '#9aa');
      ctx.font = '11px sans-serif';
      ctx.fillText(label, 8, y0 + 14);

      if (loadingLane) {
        ctx.fillStyle = '#00d4ff';
        ctx.fillText('Loading waveform…', 8, y0 + 30);
        return;
      }
      if (!buf && !underlay) {
        ctx.fillStyle = readThemeColor('--rs-muted', '#555');
        ctx.fillText('No audio', 8, y0 + 30);
        return;
      }

      const xStart = Math.max(0, timeToX(startT));
      const xEnd = Math.min(timelineWidth, timeToX(endT));

      if (underlay) {
        ctx.fillStyle = 'rgba(20, 184, 166, 0.16)';
        ctx.fillRect(xStart, y0 + h * 0.58, Math.max(0, xEnd - xStart), h * 0.42);
        if (underlay.buf) {
          drawWaveformSlice(ctx, underlay.buf, {
            timeToX,
            xToTime,
            pxPerSec,
            y0,
            height: h,
            startT,
            endT,
            color: '#5eead4',
            ampScale: underlay.gain,
            loop: true,
          });
        }
        ctx.fillStyle = colorMode === 'light' ? '#0f766e' : '#99f6e4';
        ctx.font = '10px sans-serif';
        ctx.fillText(underlay.label, xStart + 4, y0 + h - 6);
      }

      if (buf) {
        drawWaveformSlice(ctx, buf, {
          timeToX,
          xToTime,
          pxPerSec,
          y0,
          height: h,
          startT,
          endT,
          color,
          ampScale: 1,
          envFn,
        });
      }

      // Draw the ducking envelope line
      if (envFn && envWindow && envWindow.active) {
        ctx.strokeStyle = '#ffc24d';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        let started = false;
        for (let x = Math.floor(xStart); x <= xEnd; x += 2) {
          const t = xToTime(x);
          const g = envFn(t);
          const yy = y0 + 6 + (1 - g) * (h - 16);
          if (!started) {
            ctx.moveTo(x, yy);
            started = true;
          } else {
            ctx.lineTo(x, yy);
          }
        }
        ctx.stroke();
      }
    },
    [timelineWidth, timeToX, xToTime, pxPerSec, colorMode]
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const totalHeight = RULER_HEIGHT + (LANE_HEIGHT + LANE_GAP) * 3;

    if (canvas.width !== timelineWidth || canvas.height !== totalHeight) {
      canvas.width = timelineWidth;
      canvas.height = totalHeight;
    }

    ctx.fillStyle = readThemeColor('--rs-bg', '#08080c');
    ctx.fillRect(0, 0, timelineWidth, totalHeight);

    // Ruler
    ctx.fillStyle = readThemeColor('--rs-surface', '#11111a');
    ctx.fillRect(0, 0, timelineWidth, RULER_HEIGHT);
    const step = pxPerSec >= 30 ? 1 : pxPerSec >= 15 ? 2 : 5;
    for (let t = Math.ceil(tMin); t <= tMax; t += step) {
      const x = timeToX(t);
      ctx.strokeStyle = t === 0 ? '#00d4ff' : readThemeColor('--rs-border', '#262633');
      ctx.lineWidth = t === 0 ? 2 : 1;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, totalHeight);
      ctx.stroke();
      ctx.fillStyle = t === 0 ? '#00d4ff' : readThemeColor('--rs-muted', '#888');
      ctx.font = '10px monospace';
      ctx.fillText(`${t > 0 ? '+' : ''}${t}s`, x + 2, 12);
    }
    ctx.fillStyle = '#00d4ff';
    ctx.font = 'bold 10px monospace';
    ctx.fillText('JUNCTION', timeToX(0) + 4, RULER_HEIGHT - 2);

    const laneY = (i) => RULER_HEIGHT + i * (LANE_HEIGHT + LANE_GAP);

    // Previous lane (its outro tail up to the junction)
    drawBufferLane(
      ctx,
      buffers.previous,
      laneY(0),
      -prevSpan,
      0,
      '#7a8aa0',
      `PREVIOUS · ${previousItem?.title || previousItem?.asset?.title || 'Track'}`,
      loading.previous,
      (t) => envAt(t, prevDuck.start, prevDuck.end),
      prevDuck
    );

    // Voicetrack lane. A music bed, when enabled, is drawn underneath the voice.
    const bedUnderlay = bedActive
      ? {
          buf: bedBuffer,
          gain: bedGain,
          label: bedBuffer
            ? `MUSIC BED · ${bedVolumeDb} dB`
            : bedLoading
              ? 'MUSIC BED · loading…'
              : musicBed?.mode === 'specific'
                ? (musicBed.assetId ? 'MUSIC BED · unavailable' : 'MUSIC BED · pick a track')
                : `MUSIC BED · random · ${bedVolumeDb} dB`,
        }
      : null;
    drawBufferLane(
      ctx,
      buffers.voicetrack,
      laneY(1),
      vtStart,
      vtEnd,
      '#c060ff',
      bedActive ? 'VOICETRACK · OVER BED' : 'VOICETRACK',
      loading.voicetrack,
      null,
      null,
      bedUnderlay
    );
    {
      const x0 = Math.max(0, timeToX(vtStart));
      const x1 = Math.min(timelineWidth, timeToX(vtEnd));
      ctx.strokeStyle = '#c060ff';
      ctx.lineWidth = 2;
      ctx.strokeRect(x0 + 1, laneY(1) + 1, Math.max(2, x1 - x0) - 2, LANE_HEIGHT - 2);
    }
    // The part of the voice that covers the next intro.
    if (sweepNext != null && vtEnd > displayedNextStart + 0.05) {
      const x0 = Math.max(0, timeToX(Math.max(vtStart, displayedNextStart)));
      const x1 = Math.min(timelineWidth, timeToX(vtEnd));
      ctx.fillStyle = colorMode === 'light' ? 'rgba(14, 116, 144, 0.16)' : 'rgba(0, 212, 255, 0.14)';
      ctx.fillRect(x0, laneY(1), Math.max(0, x1 - x0), LANE_HEIGHT);
      ctx.fillStyle = colorMode === 'light' ? '#0e7490' : '#a5f3fc';
      ctx.font = '10px sans-serif';
      ctx.fillText('OVER INTRO', x0 + 4, laneY(1) + 28);
    }

    // Outro marker across the previous track and the voicetrack, so the voice
    // lining up with the hand-off is visible.
    if (prevOutroRel != null && prevOutroRel > tMin && prevOutroRel < 0) {
      const x = timeToX(prevOutroRel);
      ctx.strokeStyle = '#ff8c42';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(x, laneY(0));
      ctx.lineTo(x, laneY(1) + LANE_HEIGHT);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#ff8c42';
      ctx.font = '9px monospace';
      ctx.fillText('OUTRO', x + 3, laneY(1) + LANE_HEIGHT - 4);
    }

    // Next lane
    drawBufferLane(
      ctx,
      buffers.next,
      laneY(2),
      displayedNextStart,
      displayedNextStart + nextDur,
      '#7a8aa0',
      `NEXT · ${nextItem?.title || nextItem?.asset?.title || 'Track'}`,
      loading.next,
      (t) => envAt(t, nextDuck.start, nextDuck.end),
      nextDuck
    );
    // Next INTRO region (instrumental before the vocal)
    if (nextIntro > 0) {
      const x0 = Math.max(0, timeToX(displayedNextStart));
      const x1 = Math.min(timelineWidth, timeToX(displayedNextStart + nextIntro));
      ctx.fillStyle = 'rgba(0, 212, 255, 0.12)';
      ctx.fillRect(x0, laneY(2), Math.max(0, x1 - x0), LANE_HEIGHT);
    }
    // Voice ghost sitting on the ducked intro, so the sweep is visible on the next lane.
    if (sweepNext != null && sweepOverlapEnd > displayedNextStart + 0.05) {
      const x0 = Math.max(0, timeToX(displayedNextStart));
      const x1 = Math.min(timelineWidth, timeToX(sweepOverlapEnd));
      ctx.fillStyle = 'rgba(192, 96, 255, 0.28)';
      ctx.fillRect(x0, laneY(2), Math.max(0, x1 - x0), LANE_HEIGHT);
      drawWaveformSlice(ctx, buffers.voicetrack, {
        timeToX,
        xToTime,
        pxPerSec,
        y0: laneY(2),
        height: LANE_HEIGHT,
        startT: displayedNextStart,
        endT: sweepOverlapEnd,
        originT: vtStart,
        color: '#c060ff',
        ampScale: 0.85,
      });
      ctx.fillStyle = colorMode === 'light' ? '#6d28d9' : '#f3e8ff';
      ctx.font = 'bold 10px sans-serif';
      ctx.fillText('SWEEP OVER INTRO', x0 + 4, laneY(2) + 28);
    }
    if (nextIntro > 0) {
      const x0 = Math.max(0, timeToX(displayedNextStart));
      const x1 = Math.min(timelineWidth, timeToX(displayedNextStart + nextIntro));
      ctx.strokeStyle = '#00d4ff';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(x1, laneY(2));
      ctx.lineTo(x1, laneY(2) + LANE_HEIGHT);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = colorMode === 'light' ? '#0e7490' : '#00d4ff';
      ctx.font = '9px monospace';
      ctx.fillText('INTRO · vocal in', x0 + 3, laneY(2) + LANE_HEIGHT - 4);
    }
    {
      const x0 = Math.max(0, timeToX(displayedNextStart));
      const x1 = Math.min(timelineWidth, timeToX(displayedNextStart + nextDur));
      ctx.strokeStyle = readThemeColor('--rs-muted', '#5a6a80');
      ctx.lineWidth = 2;
      ctx.strokeRect(x0 + 1, laneY(2) + 1, Math.max(2, x1 - x0) - 2, LANE_HEIGHT - 2);
    }

    // Playhead
    const px = timeToX(playhead);
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px, 0);
    ctx.lineTo(px, totalHeight);
    ctx.stroke();
  }, [
    timelineWidth,
    pxPerSec,
    tMin,
    tMax,
    timeToX,
    xToTime,
    buffers,
    loading,
    prevSpan,
    prevDuck,
    prevOutroRel,
    nextDur,
    displayedNextStart,
    sweepNext,
    sweepOverlapEnd,
    nextIntro,
    nextDuck,
    vtStart,
    vtEnd,
    bedActive,
    bedBuffer,
    bedGain,
    bedVolumeDb,
    bedLoading,
    musicBed,
    previousItem,
    nextItem,
    playhead,
    drawBufferLane,
    envAt,
    colorMode,
  ]);

  useEffect(() => {
    draw();
  }, [draw]);

  // ---- Dragging ----
  const laneAtY = (y) => {
    const rel = y - RULER_HEIGHT;
    if (rel < 0) return null;
    const idx = Math.floor(rel / (LANE_HEIGHT + LANE_GAP));
    return ['previous', 'voicetrack', 'next'][idx] || null;
  };

  const handlePointerDown = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    const lane = laneAtY(y);
    const t = xToTime(x);
    const canDragNext = lane === 'next' && sweepNext == null;
    if (lane === 'voicetrack' || canDragNext) {
      setFrozenView((current) => current || { tMin, tMax });
      dragRef.current = {
        lane,
        grab: t - (lane === 'voicetrack' ? vtStart : displayedNextStart),
      };
      canvas.setPointerCapture(e.pointerId);
    } else {
      setPlayhead(t);
    }
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const t = xToTime(x);
    const snap = (v) => Math.round(v * 10) / 10;
    userMovedRef.current = true;
    if (dragRef.current.lane === 'voicetrack') {
      setVtStart(snap(t - dragRef.current.grab));
    } else if (dragRef.current.lane === 'next') {
      setNextStart(snap(t - dragRef.current.grab));
    }
  };

  const handlePointerUp = (e) => {
    if (dragRef.current && canvasRef.current) {
      try {
        canvasRef.current.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }
    dragRef.current = null;
    setFrozenView(null);
  };

  // ---- Preview playback (with ducking automation) ----
  const stopPreview = useCallback(() => {
    playSourcesRef.current.forEach(({ src }) => {
      try {
        src.stop();
      } catch (_) {}
    });
    playSourcesRef.current = [];
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setIsPlaying(false);
  }, []);

  const startPreview = useCallback(() => {
    const ctx = getCtx();
    if (ctx.state === 'suspended') ctx.resume();
    stopPreview();

    const startT = playhead;
    const now = ctx.currentTime + 0.05;
    playStartRef.current = now;
    playFromRef.current = startT;
    const toCtx = (jt) => now + (jt - startT);

    const scheduleSource = (buf, bufStartT, { envWindow, stopAt, gainScale = 1, loop = false } = {}) => {
      if (!buf) return;
      if (stopAt != null && startT >= stopAt - 0.01) return;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = !!loop;
      src.connect(gain);

      let offsetIntoBuf = startT - bufStartT;
      if (loop && buf.duration > 0 && offsetIntoBuf > 0) {
        offsetIntoBuf %= buf.duration;
      }
      if (!loop && offsetIntoBuf >= buf.duration) return;

      // Gain automation (ducking). gainScale drops a music bed under the voice.
      const initGain = (envWindow ? envAt(startT, envWindow.start, envWindow.end) : 1) * gainScale;
      gain.gain.setValueAtTime(initGain, now);
      if (envWindow && envWindow.active) {
        const f = Math.max(0.05, fadeSec);
        [
          [envWindow.start - f, 1],
          [envWindow.start, duckGain],
          [envWindow.end, duckGain],
          [envWindow.end + f, 1],
        ].forEach(([jt, val]) => {
          const ct = toCtx(jt);
          if (ct > now + 0.001) gain.gain.linearRampToValueAtTime(val * gainScale, ct);
        });
      }

      try {
        if (offsetIntoBuf >= 0) {
          src.start(now, Math.min(offsetIntoBuf, Math.max(0, buf.duration - 0.001)));
        } else {
          src.start(now - offsetIntoBuf);
        }
      } catch (err) {
        console.warn('[SegueEditor] Could not start preview source:', err.message);
        return;
      }
      if (stopAt != null) {
        const ct = toCtx(stopAt);
        if (ct > now) src.stop(ct + 0.05);
      }
      playSourcesRef.current.push({ src, gain });
    };

    // Previous: plays up to the junction, ducking under the VT over its outro
    scheduleSource(buffers.previous, -prevSpan, { envWindow: prevDuck, stopAt: 0 });
    // Music bed under the voice, looped and trimmed to the voicetrack
    if (bedBuffer && bedActive) {
      scheduleSource(bedBuffer, vtStart, { gainScale: bedGain, loop: true, stopAt: vtEnd });
    }
    // Voicetrack: full volume
    scheduleSource(buffers.voicetrack, vtStart, {});
    // Next: ducks under the voice for the sweep (or any overlap)
    scheduleSource(buffers.next, displayedNextStart, { envWindow: nextDuck });

    setIsPlaying(true);

    const tick = () => {
      const elapsed = getCtx().currentTime - playStartRef.current;
      const ph = playFromRef.current + elapsed;
      setPlayhead(ph);
      if (ph > tMax) {
        stopPreview();
        setPlayhead(playFromRef.current);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [
    getCtx,
    stopPreview,
    playhead,
    buffers,
    prevSpan,
    prevDuck,
    nextDuck,
    displayedNextStart,
    vtStart,
    vtEnd,
    bedBuffer,
    bedActive,
    bedGain,
    fadeSec,
    duckGain,
    envAt,
    tMax,
  ]);

  useEffect(() => {
    return () => {
      stopPreview();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(() => {});
      }
    };
  }, [stopPreview]);

  const resetSegue = () => {
    userMovedRef.current = false;
    const start = defaultVtStart();
    setVtStart(start);
    setNextStart(0);
    setPlayhead(start);
  };

  const fmt = (t) => `${t >= 0 ? '+' : ''}${t.toFixed(1)}s`;

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Tooltip title={isPlaying ? 'Stop preview' : 'Preview segue from playhead'}>
          <IconButton
            onClick={isPlaying ? stopPreview : startPreview}
            sx={{
              bgcolor: isPlaying ? '#ff6b6b' : '#00d4ff',
              color: '#06121a',
              '&:hover': { bgcolor: isPlaying ? '#ff5252' : '#00b8e6' },
            }}
          >
            {isPlaying ? <Stop /> : <PlayArrow />}
          </IconButton>
        </Tooltip>
        {sweepOverIntro && (
          <Chip
            size="small"
            icon={<GraphicEq sx={{ fontSize: 16 }} />}
            label={
              sweepNext != null
                ? `Sweep over intro · ${Math.min(vtDur, nextIntro).toFixed(1)}s`
                : 'Sweep over intro · no intro marker'
            }
            sx={{ bgcolor: '#00d4ff22', color: colorMode === 'light' ? '#0e7490' : '#7fdfff', fontWeight: 'medium' }}
          />
        )}
        {bedActive && (
          <Chip
            size="small"
            label={
            bedBuffer
              ? `Music bed · ${bedVolumeDb} dB`
              : musicBed?.mode === 'specific'
                ? (musicBed.assetId ? 'Music bed · unavailable' : 'Music bed · pick a track')
                : `Music bed · random · ${bedVolumeDb} dB`
          }
            sx={{ bgcolor: '#14b8a622', color: colorMode === 'light' ? '#0f766e' : '#99f6e4' }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Start the voicetrack on the outro marker">
          <IconButton size="small" onClick={resetSegue} sx={{ color: '#999' }}>
            <RestartAlt fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out">
          <IconButton size="small" onClick={() => setPxPerSec((p) => Math.max(8, p - 6))} sx={{ color: '#999' }}>
            <ZoomOut fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom in">
          <IconButton size="small" onClick={() => setPxPerSec((p) => Math.min(80, p + 6))} sx={{ color: '#999' }}>
            <ZoomIn fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Timeline */}
      <Box
        ref={wrapperRef}
        sx={{ overflowX: 'auto', bgcolor: '#08080c', borderRadius: 1, border: '1px solid #23232e' }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ display: 'block', cursor: 'grab', touchAction: 'none' }}
        />
      </Box>

      {/* Readout */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          size="small"
          label={
            prevOutroRel != null && Math.abs(vtStart - prevOutroRel) < 0.15
              ? `VT start: ${fmt(vtStart)} · on outro`
              : `VT start: ${fmt(vtStart)}${vtStart < 0 ? ' · over outro' : ''}`
          }
          sx={{ bgcolor: '#c060ff22', color: '#d9a9ff' }}
        />
        {nextIntro > 0 && (
          <Chip
            size="small"
            label={`Next intro: ${nextIntro.toFixed(1)}s${Math.abs(vtEnd - (displayedNextStart + nextIntro)) < 0.15 ? ' · ends on vocal' : ''}`}
            sx={{ bgcolor: '#00d4ff22', color: '#7fdfff' }}
          />
        )}
        <Chip
          size="small"
          icon={<GraphicEq sx={{ fontSize: 16 }} />}
          label={`Auto-duck ${duckLevelDb} dB (system default)`}
          sx={{ bgcolor: '#ffc24d22', color: '#ffd98a' }}
        />
      </Box>

      <Typography variant="caption" sx={{ color: 'var(--rs-muted)', display: 'block', mt: 1 }}>
        {prevOutroRel != null
          ? 'The voicetrack starts on the previous track’s outro marker. Drag it later to leave more of the outro uncovered.'
          : 'This track has no outro marker, so the voicetrack starts as the previous track ends. Drag it to fine-tune.'}
        {sweepOverIntro && sweepNext != null
          ? ' Sweep over intro places the next intro under the end of the voice, ducked until the vocal.'
          : sweepOverIntro
            ? ' Sweep over intro is on, but the next track has no intro marker.'
            : ''}
        {bedActive
          ? bedBuffer
            ? ` The music bed plays under the voice at ${bedVolumeDb} dB.`
            : ` A random music bed will be mixed under the voice at ${bedVolumeDb} dB when you save.`
          : ''}
        {' '}The overlapping audio dips {duckLevelDb} dB under your voice.
      </Typography>
    </Box>
  );
}
